import type { LessonBlock, LessonContent, LessonSection } from "@/lib/lesson-content/schema";
import { isInteractiveBlock } from "@/lib/ile/block-meta";

/**
 * Blocks the learner must finish before "Continue" unlocks. Tabs and the vertical roadmap are
 * the exceptions: they are optional reading and carry no points (see scoring.ts), because
 * forcing a click on each tab, or a scroll past every event, added friction without learning.
 * The filter comparison, the meaning map and tap-to-reveal tiles are optional for the same reason.
 */
export function isRequiredBlock(block: LessonBlock): boolean {
  if (!isInteractiveBlock(block.type)) return false;
  if (block.type === "vertical_roadmap" || block.type === "filter_compare" || block.type === "meaning_map" || block.type === "tile_reveal")
    return false;
  return !(block.type === "accordion_tabs" && block.data.display === "tabs");
}

export function getInteractiveBlockIds(section: LessonSection): string[] {
  return section.blocks.filter(isRequiredBlock).map((b) => b.id);
}

export function canAdvanceSection(
  section: LessonSection,
  completedBlockIds: ReadonlySet<string>,
): boolean {
  if (!section.required_to_advance) {
    return true;
  }
  return getInteractiveBlockIds(section).every((id) => completedBlockIds.has(id));
}

export function countSectionProgress(
  section: LessonSection,
  completedBlockIds: ReadonlySet<string>,
): { completed: number; total: number } {
  const interactive = getInteractiveBlockIds(section);
  const completed = interactive.filter((id) => completedBlockIds.has(id)).length;
  return { completed, total: interactive.length };
}

export function canExitLesson(
  content: LessonContent,
  sectionIndex: number,
  completedBlockIds: ReadonlySet<string>,
): boolean {
  const isLastSection = sectionIndex >= content.sections.length - 1;
  if (!isLastSection) {
    return false;
  }
  const currentSection = content.sections[sectionIndex];
  if (!currentSection) {
    return false;
  }
  return canAdvanceSection(currentSection, completedBlockIds);
}

export function countLessonProgress(
  content: LessonContent,
  completedBlockIds: ReadonlySet<string>,
): { completed: number; total: number } {
  let completed = 0;
  let total = 0;
  for (const section of content.sections) {
    const sectionProgress = countSectionProgress(section, completedBlockIds);
    completed += sectionProgress.completed;
    total += sectionProgress.total;
  }
  return { completed, total };
}

/**
 * Which sections of an `accordion_tabs` block count as visited on first render.
 *
 * The TABS variant renders its first tab already open, so requiring a click on
 * that tab before the block could complete asked the learner to "visit" the
 * panel they were already reading. The ACCORDION variant starts with every
 * panel collapsed, so nothing is seeded there — crediting a section nobody
 * expanded would let the block complete without being read.
 */
export function initialVisitedSectionIds(
  display: "accordion" | "tabs" | undefined,
  sectionIds: readonly string[],
  alreadyComplete: boolean,
): Set<string> {
  if (alreadyComplete) return new Set(sectionIds);
  // Only the tabs variant renders a panel open on load. The schema defaults
  // `display` to "accordion", so an absent value is an accordion, not tabs.
  if (display !== "tabs") return new Set();
  // Only seed when there is something left to click. Seeding the sole section of
  // a one-tab block satisfies "every section visited" on the first render, so the
  // block completes itself and credits points before the learner has even
  // reached it — and in a required_to_advance section that opens the gate too.
  if (sectionIds.length < 2) return new Set();
  return new Set([sectionIds[0]]);
}
